// GENERATED FILE — do not edit. Source of truth: contracts/. Regenerate: node scripts/sync-contracts.mjs

import { api } from '../apiClient';

import { z } from 'zod';
// Runtime validation: every named type above has a matching `<Name>Schema` zod export — the
// exact schema the backend validates with (story 2-92), so forms can `.parse()`/`.safeParse()`
// against it instead of hand-rolling checks. `.refine()` cross-field rules are preserved
// verbatim; the refine() error MESSAGE is not — the server owns user-facing text.

export type Role = {
  id: string;
  organizationId: string | null;
  key: string;
  name: string;
  description: string;
  kind: "staff" | "client";
  permissions: Array<string>;
  defaultScope: "own" | "team" | "org" | "linked";
  scopeOverrides: Record<string, string>;
  isSystem: boolean;
};
export const RoleSchema = z.object({
  id: z.string(),
  organizationId: z.string().nullable(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  kind: z.enum(["staff", "client"] as const),
  permissions: z.array(z.string()),
  defaultScope: z.enum(["own", "team", "org", "linked"] as const),
  scopeOverrides: z.record(z.string()),
  isSystem: z.boolean(),
}).strict();

export type Member = {
  id: string;
  userId: string;
  name: string;
  email: string;
  kind: "staff" | "client";
  roleIds: Array<string>;
  teamIds: Array<string>;
  status: "invited" | "active" | "suspended";
};
export const MemberSchema = z.object({
  id: z.string(),
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  kind: z.enum(["staff", "client"] as const),
  roleIds: z.array(z.string()),
  teamIds: z.array(z.string()),
  status: z.enum(["invited", "active", "suspended"] as const),
}).strict();

export type Team = {
  id: string;
  name: string;
  kind: "org-root" | "region" | "branch" | "team";
  parentTeamId: string | null;
  depth: number;
};
export const TeamSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(["org-root", "region", "branch", "team"] as const),
  parentTeamId: z.string().nullable(),
  depth: z.number(),
}).strict();

export type PermissionGroup = {
  domain: string;
  label: string;
  keys: Array<string>;
};
export const PermissionGroupSchema = z.object({
  domain: z.string(),
  label: z.string(),
  keys: z.array(z.string()),
}).strict();

export type PermissionCatalog = {
  groups: Array<PermissionGroup>;
  scopes: Array<string>;
};
export const PermissionCatalogSchema = z.object({
  groups: z.array(PermissionGroupSchema),
  scopes: z.array(z.string()),
}).strict();

export type EmailUndeliveredWarning = {
  kind: "email_undelivered";
  message: string;
  resendAvailable: boolean;
};
export const EmailUndeliveredWarningSchema = z.object({
  kind: z.literal("email_undelivered"),
  message: z.string(),
  resendAvailable: z.boolean(),
}).strict();

export type InviteMemberResult = {
  data: Member;
  warning: EmailUndeliveredWarning | null;
};
export const InviteMemberResultSchema = z.object({
  data: MemberSchema,
  warning: EmailUndeliveredWarningSchema.nullable(),
}).strict();

export type RemoveMemberResult = {
  data: {
  id: string;
  action: "revoked" | "removed";
  kind: "staff" | "client";
  seatFreed: boolean;
};
};
export const RemoveMemberResultSchema = z.object({
  data: z.object({
    id: z.string(),
    action: z.enum(["revoked", "removed"] as const),
    kind: z.enum(["staff", "client"] as const),
    seatFreed: z.boolean(),
  }).strict(),
}).strict();

export type CreateRoleBody = {
  cloneFrom: string;
  name: string;
};
export const CreateRoleBodySchema = z.object({
  cloneFrom: z.string().min(1),
  name: z.string().min(1),
}).strict();

export type UpdateRoleBody = {
  name?: string | undefined;
  permissions?: Array<string> | undefined;
  scopeOverrides?: Record<string, string> | undefined;
  defaultScope?: "own" | "team" | "org" | "linked" | undefined;
};
export const UpdateRoleBodySchema = z.object({
  name: z.string().min(1).optional(),
  permissions: z.array(z.string()).optional(),
  scopeOverrides: z.record(z.string()).optional(),
  defaultScope: z.enum(["own", "team", "org", "linked"] as const).optional(),
}).strict().refine((body) => Object.keys(body).length > 0);

export type DeleteRoleBody = {
  reassignTo?: string | undefined;
};
export const DeleteRoleBodySchema = z.object({
  reassignTo: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
}).strict();

export type DeleteRoleResult = {
  data: {
  id: string;
  reassignedCount: number;
  reassignedTo: string | null;
};
};
export const DeleteRoleResultSchema = z.object({
  data: z.object({
    id: z.string(),
    reassignedCount: z.number(),
    reassignedTo: z.string().nullable(),
  }).strict(),
}).strict();

export type UpdateMemberBody = {
  roleIds?: Array<string> | undefined;
  status?: "invited" | "active" | "suspended" | undefined;
};
export const UpdateMemberBodySchema = z.object({
  roleIds: z.array(z.string()).optional(),
  status: z.enum(["invited", "active", "suspended"] as const).optional(),
}).strict().refine((body) => Object.keys(body).length > 0);

export type InviteMemberBody = {
  email: string;
  kind?: "staff" | "client" | undefined;
  roleIds?: Array<string> | undefined;
};
export const InviteMemberBodySchema = z.object({
  email: z.string().email(),
  kind: z.enum(["staff", "client"] as const).optional(),
  roleIds: z.array(z.string()).optional(),
}).strict();

export type CreateTeamBody = {
  name: string;
  parentTeamId?: string | undefined;
};
export const CreateTeamBodySchema = z.object({
  name: z.string().min(1).max(120),
  parentTeamId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId").optional(),
}).strict();

export type ListMembersQuery = {
  cursor?: string | undefined;
  limit?: number | undefined;
  sort?: string | undefined;
  q?: string | undefined;
};
export const ListMembersQuerySchema = z.object({
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  sort: z.string().min(1).optional(),
  q: z.string().min(1).max(120).optional(),
});

export type ListRolesResponse = {
  data: Array<Role>;
};

export const ListRolesParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function listRoles(orgId: string): Promise<ListRolesResponse> {
  const response = await api.get<ListRolesResponse>(`/orgs/${encodeURIComponent(orgId)}/roles`);
  return response.data;
}

export type CreateRoleResponse = {
  data: Role;
};

export const CreateRoleParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function createRole(orgId: string, body: CreateRoleBody): Promise<CreateRoleResponse> {
  const response = await api.post<CreateRoleResponse>(`/orgs/${encodeURIComponent(orgId)}/roles`, body);
  return response.data;
}

export type UpdateRoleResponse = {
  data: Role;
};

export const UpdateRoleParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  roleId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function updateRole(orgId: string, roleId: string, body: UpdateRoleBody): Promise<UpdateRoleResponse> {
  const response = await api.patch<UpdateRoleResponse>(`/orgs/${encodeURIComponent(orgId)}/roles/${encodeURIComponent(roleId)}`, body);
  return response.data;
}

export type DeleteRoleResponse = DeleteRoleResult;

export const DeleteRoleParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  roleId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function deleteRole(orgId: string, roleId: string, body: DeleteRoleBody): Promise<DeleteRoleResponse> {
  const response = await api.delete<DeleteRoleResponse>(`/orgs/${encodeURIComponent(orgId)}/roles/${encodeURIComponent(roleId)}`, { data: body });
  return response.data;
}

export type ListMembersResponse = {
  items: Array<Member>;
  nextCursor: string | null;
  total?: number | undefined;
};

export const ListMembersParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function listMembers(orgId: string, query?: ListMembersQuery): Promise<ListMembersResponse> {
  const response = await api.get<ListMembersResponse>(`/orgs/${encodeURIComponent(orgId)}/members`, { params: query });
  return response.data;
}

export type InviteMemberResponse = InviteMemberResult;

export const InviteMemberParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function inviteMember(orgId: string, body: InviteMemberBody): Promise<InviteMemberResponse> {
  const response = await api.post<InviteMemberResponse>(`/orgs/${encodeURIComponent(orgId)}/members`, body);
  return response.data;
}

export type ResendInviteResponse = InviteMemberResult;

export const ResendInviteParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  membershipId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function resendInvite(orgId: string, membershipId: string): Promise<ResendInviteResponse> {
  const response = await api.post<ResendInviteResponse>(`/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(membershipId)}/resend-invite`);
  return response.data;
}

export type UpdateMemberResponse = {
  data: Member;
};

export const UpdateMemberParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  membershipId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function updateMember(orgId: string, membershipId: string, body: UpdateMemberBody): Promise<UpdateMemberResponse> {
  const response = await api.patch<UpdateMemberResponse>(`/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(membershipId)}`, body);
  return response.data;
}

export type RemoveMemberResponse = RemoveMemberResult;

export const RemoveMemberParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
  membershipId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function removeMember(orgId: string, membershipId: string): Promise<RemoveMemberResponse> {
  const response = await api.delete<RemoveMemberResponse>(`/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(membershipId)}`);
  return response.data;
}

export type ListTeamsResponse = {
  data: Array<Team>;
};

export const ListTeamsParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function listTeams(orgId: string): Promise<ListTeamsResponse> {
  const response = await api.get<ListTeamsResponse>(`/orgs/${encodeURIComponent(orgId)}/teams`);
  return response.data;
}

export type CreateTeamResponse = {
  data: Team;
};

export const CreateTeamParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function createTeam(orgId: string, body: CreateTeamBody): Promise<CreateTeamResponse> {
  const response = await api.post<CreateTeamResponse>(`/orgs/${encodeURIComponent(orgId)}/teams`, body);
  return response.data;
}

export type PermissionCatalogResponse = {
  data: PermissionCatalog;
};

export const PermissionCatalogParamsSchema = z.object({
  orgId: z.string().regex(new RegExp("^[0-9a-fA-F]{24}$"), "must be a Mongo ObjectId"),
});

export async function permissionCatalog(orgId: string): Promise<PermissionCatalogResponse> {
  const response = await api.get<PermissionCatalogResponse>(`/orgs/${encodeURIComponent(orgId)}/permission-catalog`);
  return response.data;
}

export const accessKeys = {
  all: ["access"] as const,
  lists: () => [...accessKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...accessKeys.lists(), ...(filters ? [filters] : [])] as const,
  details: () => [...accessKeys.all, 'detail'] as const,
  detail: (id: string) => [...accessKeys.details(), id] as const,
};
